import { useCallback, memo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareCaretLeft } from '@fortawesome/free-solid-svg-icons/faSquareCaretLeft';
import { ReducedParams } from '@api/order-reporting/types/ReducedParams';
import { ReportType } from '@api/order-reporting/types/ReportType';
import { ReducedGridData } from '@pages/reporting-pages/reduced-orders/types/ReducedGridData';
import useOrderClick from '@hooks/reportings/useOrderClick';
import { useHasPermission } from '@hooks/has-permission/useHasPermission';
import { AppRolesEnum } from '@features/routing/appRolesEnum';
import { useSaveOrderCommentMutation } from '@api/order-reporting/commentApi';
import { useSaveOrderPlanningReasonMutation, useSaveOrderExecutionReasonMutation } from '@api/order-reporting/reasonApi';
import EditableGridCell from 'components/editable-grid-cell/EditableGridCell';
import { SelectReasonCell } from 'components/select-reason-cell/SelectReasonCell';
import { SelectReasonType } from 'components/select-reason-cell/SelectReasonType';
import { ShowReasonColumn } from '@pages/reporting-pages/ShowReasonColumn';



interface GridRowProps {
	item: ReducedGridData,
	rowIndex: number,
	validParams: ReducedParams,
	showReasonColumn: ShowReasonColumn,
}
  

const GridRow = (props: GridRowProps) => {
	const { rowIndex, validParams, showReasonColumn } = props;
	const { orderCustomerSapId, name, city, street, salesMan, tlsOperator, comment, orderClientId, distributionType,
		orderUnversionedSystemId, orderRequiredDeliveryDate, orderRequiredDeliveryDateISO, orderExecutionPlace, shipmentDistributionCode, reducedBy,
		orderPlanningReasonName, orderExecutionReasonName,
		orderOrderedVolume, orderSentVolume, orderDeliveredVolume, orderedMinusSentVolume, sentMinusDeliveredVolume,
		orderOrderedCount, orderSentCount, orderDeliveredCount, orderedMinusSentCount, sentMinusDeliveredCount } = props.item;

	const hasPermission = useHasPermission();
	const orderClick = useOrderClick({ date: orderRequiredDeliveryDateISO, orderUnvId: orderUnversionedSystemId });
	const [ saveComment, { data, isError } ] = useSaveOrderCommentMutation();
	const [ savePlanningReason, { data: prData, isError: prIsError } ] = useSaveOrderPlanningReasonMutation();
	const [ saveExecutionReason, { data: erData, isError: erIsError } ] = useSaveOrderExecutionReasonMutation();

	const getClassName = useCallback((centered: boolean, leftAlign: boolean = false) => 
		`app-grid-cell${
			rowIndex % 2 == 0 ? ' secondary-cell' : ' primary-cell'
		}${
			leftAlign ? ' left-align-cell' : ''
		}${
			centered ? ' centered-cell' : ''
		}`
	,[ rowIndex ]);


	return (
		<>
			<div className={getClassName(true)}>
				{hasPermission(AppRolesEnum.orders_Read) &&
					<FontAwesomeIcon className='order-link-icon' icon={faSquareCaretLeft} onClick={orderClick} />}
			</div>

			<div className={getClassName(true)}>
				<p>{orderCustomerSapId ? orderCustomerSapId : '-'}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{name ? name : '-'}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{city ? city : '-'}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{street ? street : '-'}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{salesMan ? salesMan : '-'}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{tlsOperator ? tlsOperator : '-'}</p>
			</div>

			<EditableGridCell
				requiredRoles={AppRolesEnum.orderReports_Modify}
				className={getClassName(true)}
				isApiError={data && !data.successfull ? true : isError}
				externalValue={comment}
				onPressSave={(value: string) => saveComment({
					unversionedSystemId: orderUnversionedSystemId,
					comment: value,
					reportType: ReportType.reducedReport,
					originalArgs: validParams
				})}
			/>

			{(showReasonColumn === ShowReasonColumn.planning || showReasonColumn === ShowReasonColumn.planningAndExecution) &&
				<SelectReasonCell
					type={SelectReasonType.planningReason}
					requiredRoles={AppRolesEnum.orderReports_Modify}
					className={getClassName(true)}
					isApiError={prData && !prData.successfull ? true : prIsError}
					value={orderPlanningReasonName}
					onChange={(reason: { id: string, name: string }) => savePlanningReason({
						unversionedSystemId: orderUnversionedSystemId,
						reasonCode: reason.id,
						reasonCodeName: reason.name,
						reportType: ReportType.reducedReport,
						originalArgs: validParams
					})}
				/>
			}

			{(showReasonColumn === ShowReasonColumn.execution || showReasonColumn === ShowReasonColumn.planningAndExecution) &&
				<SelectReasonCell
					type={SelectReasonType.executionReason}
					requiredRoles={AppRolesEnum.orderReports_Modify}
					className={getClassName(true)}
					isApiError={erData && !erData.successfull ? true : erIsError}
					value={orderExecutionReasonName}
					onChange={(reason: { id: string, name: string }) => saveExecutionReason({
						unversionedSystemId: orderUnversionedSystemId,
						reasonCode: reason.id,
						reasonCodeName: reason.name,
						reportType: ReportType.reducedReport,
						originalArgs: validParams
					})}
				/>
			}

			<div className={getClassName(true)}>
				<p>{orderClientId ? orderClientId : '-'}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{orderRequiredDeliveryDate ? orderRequiredDeliveryDate : '-'}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{orderExecutionPlace ? orderExecutionPlace : '-'}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{distributionType ? distributionType : '-'}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{shipmentDistributionCode ? shipmentDistributionCode : '-'}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{reducedBy ? reducedBy : '-'}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{orderOrderedCount}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{orderSentCount}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{orderDeliveredCount}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{orderOrderedVolume}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{orderSentVolume}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{orderDeliveredVolume}</p>
			</div>
			
			<div className={getClassName(true)}>
				<p>{orderedMinusSentCount}</p>
			</div>
			
			<div className={getClassName(true)}>
				<p>{sentMinusDeliveredCount}</p>
			</div>
			
			<div className={getClassName(true)}>
				<p>{orderedMinusSentVolume}</p>
			</div>

			<div className={getClassName(true)}>
				<p>{sentMinusDeliveredVolume}</p>
			</div>
		</>
	)
}

export default memo(GridRow);